import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Edit2,
  Trash2,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Eye,
  EyeOff,
  Save,
  X,
  FolderPlus,
  Info,
  Layers,
  Crown,
  User,
  Shield,
  Wrench,
  HelpCircle,
  type LucideIcon
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import { LocaleSelector } from '@/components/LocaleSelector';
import { useAdminLocale } from '@/hooks/useAdminLocale';

const supabase = getSupabaseAdminClient();

interface FAQCategory {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
  sort_order: number;
  is_active: boolean;
}

interface FAQSubcategory {
  id: string;
  category_id: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
  sort_order: number;
  is_active: boolean;
}

const iconOptions: { value: string; label: string; icon: LucideIcon }[] = [
  { value: 'info', label: 'Info', icon: Info },
  { value: 'layers', label: 'Layers', icon: Layers },
  { value: 'crown', label: 'Crown', icon: Crown },
  { value: 'user', label: 'User', icon: User },
  { value: 'shield', label: 'Shield', icon: Shield },
  { value: 'wrench', label: 'Wrench', icon: Wrench },
  { value: 'help-circle', label: 'Help', icon: HelpCircle },
];

const getIcon = (iconName: string | null): LucideIcon => {
  const found = iconOptions.find(o => o.value === iconName);
  return found?.icon || HelpCircle;
};

export function FAQCategoriesPage() {
  const queryClient = useQueryClient();
  const { contentLocale } = useAdminLocale();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [editingCategory, setEditingCategory] = useState<FAQCategory | null>(null);
  const [editingSubcategory, setEditingSubcategory] = useState<FAQSubcategory | null>(null);
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [creatingSubcategoryFor, setCreatingSubcategoryFor] = useState<string | null>(null);

  // Fetch categories
  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ['admin-faq-categories', contentLocale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('faq_categories')
        .select('*')
        .eq('locale', contentLocale)
        .order('sort_order');
      if (error) throw error;
      return data as FAQCategory[];
    },
  });

  // Fetch subcategories
  const { data: subcategories } = useQuery({
    queryKey: ['admin-faq-subcategories', contentLocale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('faq_subcategories')
        .select('*')
        .eq('locale', contentLocale)
        .order('sort_order');
      if (error) throw error;
      return data as FAQSubcategory[];
    },
  });

  // Create category mutation
  const createCategory = useMutation({
    mutationFn: async (data: Partial<FAQCategory>) => {
      const { error } = await (supabase.from('faq_categories') as any).insert({
        name: data.name,
        slug: data.slug,
        description: data.description,
        icon: data.icon,
        sort_order: (categories?.length || 0) + 1,
        is_active: true,
        locale: contentLocale,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-faq-categories'] });
      setIsCreatingCategory(false);
      toast.success('Kategorie erstellt');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  // Update category mutation
  const updateCategory = useMutation({
    mutationFn: async (data: FAQCategory) => {
      const { error } = await (supabase.from('faq_categories') as any)
        .update({
          name: data.name,
          slug: data.slug,
          description: data.description,
          icon: data.icon,
          is_active: data.is_active,
        })
        .eq('id', data.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-faq-categories'] });
      setEditingCategory(null);
      toast.success('Kategorie aktualisiert');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  // Delete category mutation
  const deleteCategory = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('faq_categories').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-faq-categories'] });
      toast.success('Kategorie gelöscht');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  // Toggle category active
  const toggleCategoryActive = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await (supabase.from('faq_categories') as any)
        .update({ is_active })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-faq-categories'] });
    },
  });

  // Subcategory mutations
  const createSubcategory = useMutation({
    mutationFn: async (data: Partial<FAQSubcategory>) => {
      const existingSubs = subcategories?.filter(s => s.category_id === data.category_id) || [];
      const { error } = await (supabase.from('faq_subcategories') as any).insert({
        category_id: data.category_id,
        name: data.name,
        slug: data.slug,
        description: data.description,
        icon: data.icon,
        sort_order: existingSubs.length + 1,
        is_active: true,
        locale: contentLocale,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-faq-subcategories'] });
      setCreatingSubcategoryFor(null);
      toast.success('Unterkategorie erstellt');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  const updateSubcategory = useMutation({
    mutationFn: async (data: FAQSubcategory) => {
      const { error } = await (supabase.from('faq_subcategories') as any)
        .update({
          name: data.name,
          slug: data.slug,
          description: data.description,
          icon: data.icon,
          is_active: data.is_active,
        })
        .eq('id', data.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-faq-subcategories'] });
      setEditingSubcategory(null);
      toast.success('Unterkategorie aktualisiert');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  const deleteSubcategory = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('faq_subcategories').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-faq-subcategories'] });
      toast.success('Unterkategorie gelöscht');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  const toggleExpanded = (id: string) => {
    const newSet = new Set(expandedCategories);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setExpandedCategories(newSet);
  };

  if (categoriesLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="h-16 bg-slate-200 rounded"></div>
          <div className="h-16 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">FAQ-Kategorien</h1>
            <p className="text-slate-500 mt-1">
              Verwalte Haupt- und Unterkategorien für den FAQ-Bereich
            </p>
          </div>
          <LocaleSelector />
          <button
            onClick={() => setIsCreatingCategory(true)}
            className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg
                       hover:bg-teal-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Neue Kategorie
          </button>
        </div>
      </div>

      {/* Create Category Form */}
      {isCreatingCategory && (
        <CategoryForm
          onSubmit={(data) => createCategory.mutate(data)}
          onCancel={() => setIsCreatingCategory(false)}
          isLoading={createCategory.isPending}
        />
      )}

      {/* Categories List */}
      <div className="bg-white rounded-lg shadow-sm divide-y divide-slate-100">
        {categories?.map((category) => {
          const Icon = getIcon(category.icon);
          const isExpanded = expandedCategories.has(category.id);
          const categorySubs = subcategories?.filter(s => s.category_id === category.id) || [];

          return (
            <div key={category.id}>
              {/* Category Row */}
              {editingCategory?.id === category.id ? (
                <CategoryForm
                  category={editingCategory}
                  onSubmit={(data) => updateCategory.mutate({ ...editingCategory, ...data })}
                  onCancel={() => setEditingCategory(null)}
                  isLoading={updateCategory.isPending}
                  isEditing
                />
              ) : (
                <div className="flex items-center gap-4 p-4 hover:bg-slate-50">
                  <button className="text-slate-400 cursor-grab">
                    <GripVertical className="w-5 h-5" />
                  </button>

                  <button
                    onClick={() => toggleExpanded(category.id)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5" />
                    ) : (
                      <ChevronRight className="w-5 h-5" />
                    )}
                  </button>

                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center
                                  ${category.is_active ? 'bg-teal-100' : 'bg-slate-100'}`}>
                    <Icon className={`w-5 h-5 ${category.is_active ? 'text-teal-600' : 'text-slate-400'}`} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${!category.is_active && 'text-slate-400'}`}>
                        {category.name}
                      </span>
                      <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                        /{category.slug}
                      </span>
                      {categorySubs.length > 0 && (
                        <span className="text-xs text-slate-500">
                          ({categorySubs.length} Unterkategorien)
                        </span>
                      )}
                    </div>
                    {category.description && (
                      <p className="text-sm text-slate-500 truncate">{category.description}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleCategoryActive.mutate({
                        id: category.id,
                        is_active: !category.is_active
                      })}
                      className={`p-2 rounded-lg transition-colors ${
                        category.is_active
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-slate-400 hover:bg-slate-100'
                      }`}
                      title={category.is_active ? 'Deaktivieren' : 'Aktivieren'}
                    >
                      {category.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => setEditingCategory(category)}
                      className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Kategorie "${category.name}" und alle Unterkategorien löschen?`)) {
                          deleteCategory.mutate(category.id);
                        }
                      }}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Subcategories */}
              {isExpanded && (
                <div className="bg-slate-50 border-t border-slate-100">
                  {categorySubs.map((sub) => {
                    const SubIcon = getIcon(sub.icon);

                    if (editingSubcategory?.id === sub.id) {
                      return (
                        <SubcategoryForm
                          key={sub.id}
                          subcategory={editingSubcategory}
                          onSubmit={(data) => updateSubcategory.mutate({ ...editingSubcategory, ...data })}
                          onCancel={() => setEditingSubcategory(null)}
                          isLoading={updateSubcategory.isPending}
                          isEditing
                        />
                      );
                    }

                    return (
                      <div
                        key={sub.id}
                        className="flex items-center gap-4 py-3 px-4 pl-20 hover:bg-slate-100"
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center
                                        ${sub.is_active ? 'bg-white' : 'bg-slate-200'}`}>
                          <SubIcon className={`w-4 h-4 ${sub.is_active ? 'text-teal-600' : 'text-slate-400'}`} />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium text-sm ${!sub.is_active && 'text-slate-400'}`}>
                              {sub.name}
                            </span>
                            <span className="text-xs text-slate-400">/{sub.slug}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setEditingSubcategory(sub)}
                            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-white rounded"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => {
                              if (confirm(`Unterkategorie "${sub.name}" löschen?`)) {
                                deleteSubcategory.mutate(sub.id);
                              }
                            }}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {/* Add Subcategory Form */}
                  {creatingSubcategoryFor === category.id ? (
                    <SubcategoryForm
                      categoryId={category.id}
                      onSubmit={(data) => createSubcategory.mutate({ ...data, category_id: category.id })}
                      onCancel={() => setCreatingSubcategoryFor(null)}
                      isLoading={createSubcategory.isPending}
                    />
                  ) : (
                    <button
                      onClick={() => setCreatingSubcategoryFor(category.id)}
                      className="flex items-center gap-2 py-3 px-4 pl-20 text-sm text-teal-600
                                 hover:bg-teal-50 w-full transition-colors"
                    >
                      <FolderPlus className="w-4 h-4" />
                      Unterkategorie hinzufügen
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {categories?.length === 0 && (
          <div className="p-8 text-center text-slate-500">
            Noch keine Kategorien vorhanden
          </div>
        )}
      </div>
    </div>
  );
}

// Category Form Component
interface CategoryFormProps {
  category?: FAQCategory;
  onSubmit: (data: Partial<FAQCategory>) => void;
  onCancel: () => void;
  isLoading: boolean;
  isEditing?: boolean;
}

function CategoryForm({ category, onSubmit, onCancel, isLoading, isEditing }: CategoryFormProps) {
  const [name, setName] = useState(category?.name || '');
  const [slug, setSlug] = useState(category?.slug || '');
  const [description, setDescription] = useState(category?.description || '');
  const [icon, setIcon] = useState(category?.icon || 'help-circle');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ name, slug, description, icon });
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  return (
    <form onSubmit={handleSubmit} className="bg-teal-50 p-4 border-y border-teal-100">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!isEditing) setSlug(generateSlug(e.target.value));
            }}
            required
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm
                       focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            placeholder="z.B. Allgemeines"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Slug</label>
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            required
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm
                       focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            placeholder="z.B. allgemeines"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Icon</label>
          <select
            value={icon}
            onChange={(e) => setIcon(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm
                       focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          >
            {iconOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Beschreibung</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm
                       focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            placeholder="Kurze Beschreibung..."
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
        >
          <X className="w-4 h-4" />
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-teal-500 text-white rounded-lg
                     hover:bg-teal-600 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {isEditing ? 'Speichern' : 'Erstellen'}
        </button>
      </div>
    </form>
  );
}

// Subcategory Form Component
interface SubcategoryFormProps {
  subcategory?: FAQSubcategory;
  categoryId?: string;
  onSubmit: (data: Partial<FAQSubcategory>) => void;
  onCancel: () => void;
  isLoading: boolean;
  isEditing?: boolean;
}

function SubcategoryForm({ subcategory, categoryId, onSubmit, onCancel, isLoading, isEditing }: SubcategoryFormProps) {
  const [name, setName] = useState(subcategory?.name || '');
  const [slug, setSlug] = useState(subcategory?.slug || '');
  const [description, setDescription] = useState(subcategory?.description || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ name, slug, description, category_id: categoryId });
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  return (
    <form onSubmit={handleSubmit} className="bg-teal-50/50 py-3 px-4 pl-20 border-y border-teal-100/50">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!isEditing) setSlug(generateSlug(e.target.value));
            }}
            required
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm
                       focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            placeholder="Name der Unterkategorie"
          />
        </div>
        <div>
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            required
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm
                       focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            placeholder="Slug"
          />
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm
                       focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            placeholder="Beschreibung (optional)"
          />
          <button
            type="button"
            onClick={onCancel}
            className="px-2 py-2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-4 h-4" />
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-3 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
          </button>
        </div>
      </div>
    </form>
  );
}
